/**
 * Operating notices — D5, and the part of this module that is **not** history.
 *
 * ## The distinction that shapes everything below
 *
 * An entry is history: it is written once, it stays for ever, and the panel is where somebody
 * goes to catch up. A notice is the opposite in every respect — it is about right now, it
 * expires, and then it is gone without a trace. **A notice therefore never becomes an entry,
 * never enters the history, and leaves nothing behind when it ends** (plan §5, W5). Anything
 * that blurs the two turns the panel into a log of Tuesday's maintenance windows, and nobody
 * reads a history of things that are no longer true.
 *
 * **Since M3 of the Reporting App's central-notices plan (D5 there, 2026-09-24) the panel shows
 * the notices that are valid RIGHT NOW**, in a section of its own above the entries — so a
 * notice closed here can still be read there. That is not history: the section holds exactly
 * what the wire holds, an expired notice appears nowhere, and closing the bar changes nothing
 * in the panel. Dismissing stays a property of the bar alone. The dot on the button does not
 * light for a notice (D8 there): the bar is how a notice announces itself.
 *
 * Both kinds of notice arrive here through the same shape and are deliberately
 * indistinguishable: the WRITTEN ones — since M3 posted on the Reporting App's `/notices` page
 * and fetched by this app's API host — and a CALCULATED one, where an app has one (this one does
 * not), which has no row anywhere. The bar does not know which is which and must not start
 * guessing — see `types.ts`.
 *
 * ## Closing it, and what "until it changes" means
 *
 * A notice is dismissible, and a dismissal holds until the notice CHANGES. That is not the id:
 * the calculated notice keeps one stable id for ever, so dismissing it once would silence it
 * permanently. It is a fingerprint of everything the reader actually saw — severity, title,
 * body and the end of the window. Edit the text, extend the window, or let the next cadence
 * cycle move `validUntil`, and the bar comes back, which is what somebody re-posting a notice
 * expects to happen.
 *
 * **The dismissal lives in `localStorage`, and that is a deliberate departure from D3.** The
 * seen-state of entries follows the person across devices because a marker seen twice is a
 * marker devalued, and it is worth a table for that. A notice is worth neither a table nor a
 * write path: D2 and D5 rule out a write path in the app for exactly this area, adding one
 * would be the admin UI those decisions rejected, and the cost of the weaker store is that a
 * maintenance banner closed on a laptop is closed again on a phone — once, for something that
 * expires by itself within hours. Cleared browser data loses the dismissals and nothing else.
 *
 * ## It disappears by itself
 *
 * The bar schedules a timer for the earliest `validUntil` it is showing, so a notice that
 * expires while somebody has the tab open goes away on its own rather than sitting there being
 * wrong until the next reload. That is the whole point of an expiring notice, and a reload is
 * not something this audience does — the app is opened before a call and left open.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Info, X } from 'lucide-react';
import { useWhatsNew } from './WhatsNewProvider';
import type { Notice, NoticeSeverity } from './types';

/** One key for the whole module, holding a map of id → the fingerprint that was dismissed. */
const STORAGE_KEY = 'whats-new.dismissed-notices';

/**
 * What the reader saw, in one short string.
 *
 * Not a hash: a hash would need a dependency or a hand-rolled one, and the whole map is a
 * handful of notices. The severity is in it because a notice promoted from `info` to
 * `critical` is a different notice, whatever its text says.
 */
function fingerprint(notice: Notice): string {
    return `${notice.severity}|${notice.title}|${notice.body}|${notice.validUntil}`;
}

/**
 * Both accessors are wrapped, because `localStorage` throws rather than returning null in a
 * locked-down browser profile, and a notice bar may never be the reason a screen does not
 * render. The failure direction is "show it", which is right for an operating notice: seeing a
 * maintenance banner twice costs a click, missing it costs the maintenance.
 */
function readDismissed(): Record<string, string> {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        const parsed: unknown = raw ? JSON.parse(raw) : null;
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
            ? (parsed as Record<string, string>)
            : {};
    } catch {
        return {};
    }
}

function writeDismissed(next: Record<string, string>): void {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
        /* the notice simply reappears on the next load */
    }
}

/**
 * The palette says what the severity means, and nothing else does (design.md, R1 of
 * `scan-design-tokens.mjs`): amber is "you have to account for this", red is "stop", and the
 * neutral blue is "for your information". Three states, three colours, no fourth.
 */
export const NOTICE_STYLES: Record<NoticeSeverity, { bar: string; icon: string; button: string }> = {
    info: {
        bar: 'border-blue-200 bg-blue-50 text-blue-900',
        icon: 'text-blue-600',
        button: 'text-blue-700 hover:bg-blue-100',
    },
    warning: {
        bar: 'border-amber-200 bg-amber-50 text-amber-900',
        icon: 'text-amber-600',
        button: 'text-amber-800 hover:bg-amber-100',
    },
    critical: {
        bar: 'border-red-200 bg-red-50 text-red-900',
        icon: 'text-red-600',
        button: 'text-red-800 hover:bg-red-100',
    },
};

export function NoticeBar() {
    const { notices, allNotices } = useWhatsNew();
    const [dismissed, setDismissed] = useState<Record<string, string>>(() => readDismissed());
    /** Bumped when a notice's window ends, so the list below is re-filtered against the clock. */
    const [tick, setTick] = useState(0);

    const visible = useMemo(
        () => {
            const now = Date.now();
            return notices.filter(n => {
                const until = Date.parse(n.validUntil);
                // An unparseable date is shown rather than hidden: the same direction as the
                // storage failures above, and a malformed notice is still somebody trying to
                // say something.
                if (!Number.isNaN(until) && until <= now) return false;
                return dismissed[n.id] !== fingerprint(n);
            });
            // `tick` is a dependency on purpose: it is the clock, and without it a notice that
            // expires while the tab is open would stay on the screen being wrong.
        },
        [notices, dismissed, tick],
    );

    /**
     * One timer, for the earliest end among the notices actually on screen. Not an interval:
     * this app is left open for hours, and a poll that runs all afternoon to catch an event
     * whose exact time is already known is a poll nobody would write down as a decision.
     */
    useEffect(() => {
        const ends = visible
            .map(n => Date.parse(n.validUntil))
            .filter(t => !Number.isNaN(t) && t > Date.now());
        if (ends.length === 0) return;
        // `setTimeout` is a 32-bit millisecond count: anything past ~24.8 days fires
        // immediately, which would be a render loop rather than a long wait.
        const delay = Math.min(Math.min(...ends) - Date.now() + 1000, 2_000_000_000);
        const timer = window.setTimeout(() => setTick(t => t + 1), delay);
        return () => window.clearTimeout(timer);
    }, [visible]);

    const dismiss = useCallback((notice: Notice) => {
        setDismissed(prev => {
            const next = { ...prev, [notice.id]: fingerprint(notice) };
            // Ids that are no longer on the wire are dropped here, so this key cannot grow for
            // ever: a notice that has expired can never come back, so its dismissal is dead
            // weight. Nothing else cleans it up — there is no server-side state to clean.
            // Against ALL notices, not the page-filtered ones (`reporting_app`): pruning against
            // this page's list would forget the dismissal of a notice that belongs to another page.
            const live = new Set(allNotices.map(n => n.id));
            for (const id of Object.keys(next)) if (!live.has(id)) delete next[id];
            writeDismissed(next);
            return next;
        });
    }, [allNotices]);

    if (visible.length === 0) return null;

    return (
        <div className="flex flex-col">
            {visible.map(notice => {
                const style = NOTICE_STYLES[notice.severity] ?? NOTICE_STYLES.info;
                const Icon = notice.severity === 'info' ? Info : AlertTriangle;
                return (
                    <div
                        key={notice.id}
                        role="status"
                        aria-live="polite"
                        data-notice-id={notice.id}
                        className={`flex items-start gap-3 border-b px-6 py-2.5 ${style.bar}`}
                    >
                        <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${style.icon}`} aria-hidden="true" />
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-bold leading-tight">{notice.title}</p>
                            <p className="mt-0.5 text-sm leading-relaxed">{notice.body}</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => dismiss(notice)}
                            aria-label={`Dismiss notice: ${notice.title}`}
                            className={`-mr-1 shrink-0 rounded-md p-1.5 ${style.button}`}
                        >
                            <X className="h-4 w-4" aria-hidden="true" />
                        </button>
                    </div>
                );
            })}
        </div>
    );
}
